import React, { useState, useCallback } from 'react';
import { Modal, Button, Upload, Spin, message } from 'antd';
import type { UploadProps } from 'antd/es/upload/interface';
import { CloseOutlined } from '@ant-design/icons';
import { useFileUpload, FileUploadResult } from 'hooks/useFileUpload';
import { uploadProviderPrivateFile } from 'api/common';
import { submitImportBatch } from 'api/group';
import { IFileUploadResult } from 'types/inbox';
import uploadTemplateImg from 'assets/groupPractice/uploadTemplate.png';
import s from './s.module.less';

type Props = {
    open: boolean;
    onClose: () => void;
    onUploadComplete: (batchUid: string) => void;
};

const ExternalLinkIcon = () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
            d="M6.667 3.333H3.333v9.334h9.334V9.333"
            stroke="#374151"
            strokeWidth="1.2"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
        <path d="M9.333 2.667h4v4" stroke="#374151" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
        <path
            d="M6.667 9.333L13.333 2.667"
            stroke="#374151"
            strokeWidth="1.2"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

const StepSeparator = () => (
    <svg width="12" height="80" viewBox="0 0 12 80" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M1 0V32L11 40L1 48V80" stroke="#D1D5DB" strokeWidth="1" />
    </svg>
);

const CheckIcon = () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M6 10L9 13L14 7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const DocumentIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
            d="M9 12H15M9 16H15M17 21H7C5.89543 21 5 20.1046 5 19V5C5 3.89543 5.89543 3 7 3H12.5858C12.851 3 13.1054 3.10536 13.2929 3.29289L18.7071 8.70711C18.8946 8.89464 19 9.149 19 9.41421V19C19 20.1046 18.1046 21 17 21Z"
            stroke="#111827"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

const UploadFileIcon = () => (
    <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
            d="M28 8H12C10.8954 8 10 8.89543 10 10V38C10 39.1046 10.8954 40 12 40H36C37.1046 40 38 39.1046 38 38V18L28 8Z"
            stroke="#9CA3AF"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
        <path d="M28 8V18H38" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M24 34V24" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M19 29L24 24L29 29" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

type StepConfig = {
    number: string;
    title: string;
    desc: string;
};

const STEPS: StepConfig[] = [
    { number: '01', title: 'PREPARE', desc: 'Copy template and enter provider info' },
    { number: '02', title: 'UPLOAD', desc: 'Upload .xlsx file' },
    { number: '03', title: 'CONFIRM', desc: 'Confirm provider info' },
];

const DirectUploadModal: React.FC<Props> = ({ open, onClose, onUploadComplete }) => {
    const [currentStep, setCurrentStep] = useState(0);
    const [fileName, setFileName] = useState('');
    const [uploadResult, setUploadResult] = useState<FileUploadResult | null>(null);
    const [submitting, setSubmitting] = useState(false);

    const { loading, uploadFile: uploadFileToServer } = useFileUpload({
        accept: '.xlsx',
        maxSize: 10,
        uploadFn: uploadProviderPrivateFile,
        formData: {
            uploadTypeEnum: 'ORGANIZATION_PROVIDER_IMPORT',
        },
        formatFileData: async (result: IFileUploadResult) => {
            const data: FileUploadResult = {
                id: result.id,
                url: result.fileUri,
                name: result.fileOriginName,
                type: result.fileExtension,
            };
            return data;
        },
        onSuccess: (result) => {
            setUploadResult(result);
        },
    });

    const handleNext = useCallback(async () => {
        if (!uploadResult?.id) return;
        setSubmitting(true);
        try {
            const submitRes = await submitImportBatch(uploadResult.id);
            const uid = submitRes?.data?.data?.batchUid;
            if (!uid) {
                message.error('Failed to create import batch');
                return;
            }
            onUploadComplete(uid);
        } catch {
            message.error('Failed to create import batch');
        } finally {
            setSubmitting(false);
        }
    }, [uploadResult, onUploadComplete]);

    const handleClose = useCallback(() => {
        setCurrentStep(0);
        setFileName('');
        setUploadResult(null);
        setSubmitting(false);
        onClose();
    }, [onClose]);

    const handleUploadChange: UploadProps['onChange'] = useCallback(
        async (info) => {
            const file = info.fileList.slice(-1)[0]?.originFileObj;
            if (file) {
                setFileName(file.name);
                const result = await uploadFileToServer(file);
                if (!result) {
                    setFileName('');
                }
            }
        },
        [uploadFileToServer],
    );

    const handleRemoveFile = useCallback(() => {
        setFileName('');
        setUploadResult(null);
    }, []);

    const handleStepClick = useCallback(
        (i: number) => {
            if (i <= currentStep) {
                setCurrentStep(i);
            }
        },
        [currentStep],
    );

    const uploadProps: UploadProps = {
        name: 'file',
        multiple: false,
        accept: '.xlsx, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        showUploadList: false,
        onChange: handleUploadChange,
        beforeUpload: () => false,
    };

    const renderStepCircle = (step: StepConfig, i: number) => {
        if (i < currentStep) {
            return (
                <div className={s.stepCircleDone}>
                    <CheckIcon />
                </div>
            );
        }
        return <div className={`${s.stepCircle} ${i === currentStep ? s.stepCircleActive : ''}`}>{step.number}</div>;
    };

    return (
        <Modal
            open={open}
            closable={false}
            footer={null}
            width={1150}
            destroyOnClose
            className={s.modal}
            onCancel={handleClose}
            zIndex={99999}
        >
            <div className={s.container}>
                <div className={s.header}>
                    <span className={s.headerTitle}>Upload provider profile</span>
                    <span className={s.closeBtn} onClick={handleClose}>
                        <CloseOutlined />
                    </span>
                </div>

                <div className={s.body}>
                    <div className={s.stepper}>
                        {STEPS.map((step, i) => (
                            <React.Fragment key={i}>
                                {i > 0 && (
                                    <div className={s.separatorWrap}>
                                        <StepSeparator />
                                    </div>
                                )}
                                <div
                                    className={`${s.stepPanel} ${i === currentStep ? s.stepPanelActive : ''}`}
                                    onClick={() => handleStepClick(i)}
                                >
                                    {renderStepCircle(step, i)}
                                    <div className={s.stepText}>
                                        <div className={`${s.stepTitle} ${i <= currentStep ? s.stepTitleActive : ''}`}>
                                            {step.title}
                                        </div>
                                        <div className={s.stepDesc}>{step.desc}</div>
                                    </div>
                                    {i === currentStep && <div className={s.stepBar} />}
                                </div>
                            </React.Fragment>
                        ))}
                    </div>

                    {currentStep === 0 && (
                        <div className={s.stepContent}>
                            <div className={s.instruction}>
                                <h4 className={s.instructionTitle}>Step1: Access our Google Sheets template</h4>
                                <p className={s.instructionDesc}>
                                    Click the button below to open the template and enter provider information. The
                                    template includes the recommended fields needed to create provider profiles on
                                    Klarity.
                                </p>
                                <Button
                                    className={s.templateBtn}
                                    onClick={() =>
                                        window.open(
                                            'https://docs.google.com/spreadsheets/d/1qoPV4fPotnDOUelXZSJmSK-yc26rw8E59qkKIlxJVhU/edit?usp=sharing',
                                            '_blank',
                                        )
                                    }
                                >
                                    Open Google Sheets template <ExternalLinkIcon />
                                </Button>
                            </div>

                            <div className={s.instruction}>
                                <h4 className={s.instructionTitle}>Step 2: Make a copy to your drive</h4>
                                <p className={s.instructionDesc}>
                                    {`Click "File" and select "Make a copy" to create your own patient contact sheet. If "Make a Copy" is unavailable, please sign in to your Google account and retry.`}
                                </p>
                                <img src={uploadTemplateImg} alt="Template preview" className={s.templateImg} />
                            </div>

                            <div className={s.instruction}>
                                <h4 className={s.instructionTitle}>
                                    Step 3: Enter provider details and download as .xlsx file
                                </h4>
                                <p className={s.instructionDesc}>
                                    Fill in the provider information in the template, then download the file as a .xlsx
                                    (Excel) file. Each row represents one provider profile.
                                </p>
                                <p className={s.instructionDesc}>You can upload multiple providers at once.</p>
                            </div>

                            <p className={s.helpText}>
                                Need help? <span className={s.contactLink}>Contact us</span>
                            </p>
                        </div>
                    )}

                    {currentStep === 1 && (
                        <div className={s.stepContent}>
                            <h4 className={s.uploadTitle}>Upload .xlsx file</h4>
                            {uploadResult ? (
                                <div className={s.fileCard}>
                                    <div className={s.fileInfo}>
                                        <DocumentIcon />
                                        <span>{fileName}</span>
                                    </div>
                                    <Button className={s.removeBtn} onClick={handleRemoveFile}>
                                        Remove
                                    </Button>
                                </div>
                            ) : (
                                <div className={s.uploadArea}>
                                    {loading && (
                                        <div className={s.loadingMask}>
                                            <Spin />
                                        </div>
                                    )}
                                    <Upload.Dragger {...uploadProps} className={s.uploadDragger}>
                                        <div className={s.uploadContent}>
                                            <UploadFileIcon />
                                            <p className={s.uploadText}>
                                                <span className={s.uploadLink}>Upload a file</span> or drag and drop
                                            </p>
                                            <p className={s.uploadHint}>.xlsx up to 10MB</p>
                                        </div>
                                    </Upload.Dragger>
                                </div>
                            )}
                        </div>
                    )}

                    {currentStep === 2 && (
                        <div className={s.stepContent}>
                            <div className={s.placeholder}>Confirm step content — coming soon</div>
                        </div>
                    )}
                </div>

                <div className={s.footer}>
                    <div className={s.footerBorder} />
                    <div className={currentStep >= 1 ? s.footerActionsBetween : s.footerActions}>
                        <Button
                            className={s.backBtn}
                            onClick={currentStep === 0 ? handleClose : () => setCurrentStep(currentStep - 1)}
                        >
                            Back
                        </Button>
                        {currentStep === 0 && (
                            <Button type="primary" className={s.primaryBtn} onClick={() => setCurrentStep(1)}>
                                Continue
                            </Button>
                        )}
                        {currentStep === 1 && (
                            <Button
                                type="primary"
                                disabled={!uploadResult || submitting}
                                loading={submitting}
                                className={uploadResult ? s.primaryBtn : s.disabledBtn}
                                onClick={handleNext}
                            >
                                Next
                            </Button>
                        )}
                        {currentStep === 2 && (
                            <Button type="primary" className={s.primaryBtn}>
                                Next
                            </Button>
                        )}
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default DirectUploadModal;
